/* eslint-disable @typescript-eslint/no-explicit-any */
 
import axios, { AxiosRequestConfig } from 'axios';

const baseURL = import.meta.env.VITE_APP_API_BE_URL

// Define the standard API response format
export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  error: string | null;
  timestamp: string;
}

// Create an axios instance
const api = axios.create({
  baseURL: baseURL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 100000,
  withCredentials: true, // Include credentials (cookies) in requests
});

// Add request interceptor to inject auth token
api.interceptors.request.use(config => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for token refresh and error handling
api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    
    // Handle 401 (Unauthorized) errors with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Get refresh token
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        // No refresh token available
        return Promise.reject(error);
      }
      
      try {
        // Call refresh token API
        const refreshResponse = await axios.post(`${baseURL}auth/refresh`, {
          refreshToken
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (refreshResponse.status === 200) {
          // Store new tokens
          const { accessToken, refreshToken: newRefreshToken } = refreshResponse.data.data;
          localStorage.setItem('auth_token', accessToken);
          if (newRefreshToken) {
            localStorage.setItem('refresh_token', newRefreshToken);
          }
          
          // Retry original request with new token
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          return api(originalRequest);
        } else {
          // Clear tokens on failure
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('isAuthenticated');
          window.location.href = '/login';
          return Promise.reject(error);
        }
      } catch (refreshError) {
        console.error('Failed to refresh token:', refreshError);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(error);
      }
    }
    
    // Format error responses
    if (error.response) {
      try {
        const errorData = error.response.data;
        return Promise.reject({
          status: error.response.status,
          statusText: error.response.statusText,
          message: errorData.message || 'Có lỗi xảy ra',
          error: errorData.error || null,
          timestamp: errorData.timestamp || new Date().toISOString()
        });
      } catch (e) {
        console.log(e);
        return Promise.reject({
          status: error.response.status,
          statusText: error.response.statusText,
          message: 'Có lỗi xảy ra',
          error: null,
          timestamp: new Date().toISOString()
        });
      }
    }
    
    return Promise.reject(error);
  }
);

export const apiClient = {
  get: async <T>(url: string, options?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    try {
      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Lỗi không xác định',
        data: null as T,
        error: error.error || error.toString(),
        timestamp: error.timestamp || new Date().toISOString()
      };
    }
  },

  post: async <T>(url: string, data?: unknown, options?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await api.post(url, data, options);
    return response.data;
  },

  postFormData: async <T>(url: string, data?: FormData, options?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const formOptions = {
      ...options,
      headers: {
        ...options?.headers,
        'Content-Type': 'multipart/form-data'
      }
    };
    const response = await api.post(url, data, formOptions);
    return response.data;
  },

  put: async <T>(url: string, data?: unknown, options?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await api.put(url, data, options);
    return response.data;
  },

  patch: async <T>(url: string, data?: unknown, options?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await api.patch(url, data, options);
    return response.data;
  },

  delete: async <T>(url: string, options?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await api.delete(url, options);
    return response.data;
  },
};

export default api;
